import {Action, createFeatureSelector, createReducer, createSelector, on} from '@ngrx/store';
import * as fromActions from './user-management-actions';
import {AppStateNameEnum} from '@core/enums/app-state-name.enum';
import {UserDataModel} from '@core/modules/rest/user/response/user-data.model';

export const REDUCER_NAME = AppStateNameEnum.feature_users;

export interface State {
    users: UserDataModel[];
    loading: boolean;
}

const initialState: State = {
    users: [],
    loading: false
};

const stateSelector = createFeatureSelector<State>(REDUCER_NAME);

export const organizationUsersDataSelector = createSelector(stateSelector, (state: State) => state.users);

const innerReducer = createReducer(
    initialState,
    on(fromActions.ResetState, ((): State => initialState)),
    on(fromActions.GetUsersByOrganization, ((state: State): State => {
        return ({
            ...state,
            loading: true
        });
    })),
    on(fromActions.GetUsersByOrganizationSuccess, ((state: State, {users}): State => ({
        ...state,
        users: users.concat(state.users),
        loading: false
    }))),
    on(fromActions.GetFilteredUsersByOrganization, ((state: State): State => {
        return ({
            ...state,
            loading: true
        });
    })),
    on(fromActions.GetFilteredUsersByOrganizationSuccess, ((state: State, {users}): State => ({
        ...state,
        users,
        loading: false
    }))),
    on(fromActions.CreateUser, ((state: State): State => {
        return ({
            ...state,
            loading: true
        });
    })),
    on(fromActions.CreateUserSuccess, ((state: State, {newUser}): State => ({
        users: [newUser, ...state.users],
        loading: false
    }))),
    on(fromActions.AlterUserStatus, ((state: State): State => {
        return ({
            ...state,
            loading: true
        });
    })),
    on(fromActions.AlterUserStatusSuccess, ((state: State, {alteredUser}): State => ({
        ...state,
        users: state.users.map(user => user.userId === alteredUser.userId ? alteredUser : user),
        loading: false
    }))),
    on(fromActions.DeleteUser, ((state: State): State => {
        return ({
            ...state,
            loading: true
        });
    })),
    on(fromActions.DeleteUserSuccess, ((state: State, {userId}): State => ({
        ...state,
        users: state.users.filter(user => user.userId !== userId),
        loading: false
    }))),
    on(fromActions.UpdateUserRoles, ((state: State): State => {
        return ({
            ...state,
            loading: true
        });
    })),
    on(fromActions.UpdateUserRolesSuccess, ((state: State, {userId, newRoles}): State => ({
        ...state,
        users: state.users.map(user => {
            if (user.userId === userId) {
                return {
                    ...user,
                    roles: newRoles
                };
            }
            return user;
        }),
        loading: false
    }))),
    on(fromActions.SaveUserEducationSettingsSuccess, ((state: State, {updatedUser}): State => ({
        ...state,
        users: state.users.map(user => {
            if (user.userId === updatedUser.userId) {
                return {
                    ...user,
                    settings: updatedUser.settings,
                    personalSettings: updatedUser.personalSettings
                };
            }
            return user;
        }),
        loading: false
    }))),
    on(fromActions.UpdateUserSettingsSuccess, ((state: State, {userId, settings, personalSettings}): State => ({
        ...state,
        users: state.users.map(user => {
            if (user.userId === userId) {
                return {
                    ...user,
                    settings,
                    personalSettings
                };
            }
            return user;
        }),
        loading: false
    })))
);

export function reducer(state: State | undefined, action: Action): State {
    return innerReducer(state, action);
}

